import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, Bot, LifeBuoy, Send, Sparkles, Trash2, X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const normalizeQuestion = (value) => {
  const aliases = {
    consiliacion: 'conciliacion', conciliasion: 'conciliacion', concilacion: 'conciliacion',
    insidencia: 'incidencia', incidensia: 'incidencia', mantenimento: 'mantenimiento',
    manteminiento: 'mantenimiento', difucion: 'difusion', cotisacion: 'cotizacion',
    sansion: 'sancion', docuemnto: 'documento', probedor: 'proveedor', asistensia: 'asistencia',
  };
  return String(value || '').trim().toLocaleLowerCase('es-MX').normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ')
    .split(' ').map((token) => aliases[token] || token).join(' ');
};

const sanitizeAssistantText = (value) => String(value || '')
  // Los identificadores entre corchetes pertenecen a los botones internos de
  // WhatsApp. Nunca deben mostrarse en las conversaciones de la plataforma.
  .replace(/\s*\[#[^\]\r\n]+\]/g, '')
  .replace(/[ \t]+\n/g, '\n')
  .replace(/\n{3,}/g, '\n\n')
  .trim();

const normalizeReply = (value) => {
  const reply = sanitizeAssistantText(value);
  if (!reply) return 'Dime qué deseas consultar o registrar y te llevaré a la opción disponible para tu perfil.';
  if (/[.!?…:)\]]$/.test(reply)) return reply;
  const lastComplete = Math.max(reply.lastIndexOf('.'), reply.lastIndexOf('?'), reply.lastIndexOf('!'));
  const completed = lastComplete >= 10 ? reply.slice(0, lastComplete + 1) : reply;
  return `${completed}\n\nSi necesitas el procedimiento exacto, indícame la acción y la pantalla en la que estás.`;
};

export default function GlobalSupportCoach({ condominioId, condominioName }) {
  const { api, user, token } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [waitingSeconds, setWaitingSeconds] = useState(0);
  const activeRoles = useMemo(() => (user?.roles || []).filter((item) => item?.is_active !== false).map((item) => item?.role), [user?.roles]);
  const isCouncil = useMemo(() => {
    return activeRoles.includes('consejo') && !activeRoles.some((role) => ['direccion', 'supervisor', 'administrador'].includes(role));
  }, [activeRoles]);
  const profile = useMemo(() => {
    if (isCouncil) return 'council';
    if (activeRoles.includes('direccion')) return 'direction';
    if (activeRoles.includes('supervisor')) return 'supervisor';
    return 'administrator';
  }, [activeRoles, isCouncil]);
  const [liveModules, setLiveModules] = useState(null);
  useEffect(() => {
    let current = true;
    setLiveModules(null);
    if (!isCouncil || !condominioId) return () => { current = false; };
    api.get(`/modulos/mis-modulos?condominio_id=${encodeURIComponent(condominioId)}`)
      .then((response) => { if (current) setLiveModules(response.data?.modulos || {}); })
      .catch(() => { if (current) setLiveModules({}); });
    return () => { current = false; };
  }, [api, isCouncil, condominioId]);
  const councilCapabilities = useMemo(() => {
    const modules = user?.effective_permissions?.by_condominio?.[condominioId]?.modulos || {};
    const allowed = (key) => Boolean(modules?.[key]?.can_view);
    return {
      finance: Boolean(liveModules?.financiero) && (allowed('financiero') || allowed('finanzas')),
      agreements: allowed('acuerdos') || allowed('minutas'),
      operation: allowed('incidencias') || allowed('mantenimientos') || allowed('alertas'),
      documents: allowed('documentos'),
    };
  }, [user?.effective_permissions, condominioId, liveModules]);
  const councilTopics = useMemo(() => [
    councilCapabilities.agreements ? 'acuerdos y decisiones' : null,
    councilCapabilities.finance ? 'situación financiera' : null,
    councilCapabilities.operation ? 'seguimiento operativo' : null,
    councilCapabilities.documents ? 'documentos publicados' : null,
  ].filter(Boolean), [councilCapabilities]);
  const welcomeMessage = useMemo(() => ({
    role: 'assistant',
    content: isCouncil
      ? `Hola. Soy el asistente del Consejo. Puedo ayudarte con ${councilTopics.join(', ') || 'la información publicada para tu perfil'} del condominio y llevarte directamente a las consultas autorizadas.`
      : profile === 'supervisor'
        ? 'Hola. Puedo ayudarte a consultar y dar seguimiento a tus condominios, incidencias, mantenimientos, acuerdos y tareas pendientes. Solo te mostraré funciones disponibles para Supervisión.'
        : profile === 'direction'
          ? 'Hola. Puedo llevarte a la decisión, revisión o reporte que necesitas y explicarte el siguiente paso dentro de AR Gestión.'
          : 'Hola. Puedo ayudarte a consultar, registrar y dar seguimiento a la operación del condominio. Dime qué necesitas y te llevaré a la pantalla correcta.',
  }), [isCouncil, councilTopics, profile]);
  const storageKey = useMemo(() => `ar_support_coach_${user?.id || 'usuario'}`, [user?.id]);
  const [messages, setMessages] = useState([welcomeMessage]);
  const endRef = useRef(null);

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(storageKey) || '[]');
      const restored = Array.isArray(stored) ? stored.slice(-20).map((message) => (
        message?.role === 'assistant'
          ? { ...message, content: sanitizeAssistantText(message.content) }
          : message
      )).filter((message) => message?.content) : [];
      setMessages(restored.length ? restored : [welcomeMessage]);
    } catch {
      setMessages([welcomeMessage]);
    }
  }, [storageKey, welcomeMessage]);

  useEffect(() => {
    const openAssistant = (event) => {
      setOpen(true);
      if (event?.detail?.prompt) setInput(String(event.detail.prompt));
    };
    window.addEventListener('ar:open-assistant', openAssistant);
    return () => window.removeEventListener('ar:open-assistant', openAssistant);
  }, []);

  useEffect(() => {
    if (messages.length) localStorage.setItem(storageKey, JSON.stringify(messages.slice(-20)));
    if (open) endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open, storageKey]);

  useEffect(() => {
    if (!loading) {
      setWaitingSeconds(0);
      return undefined;
    }
    const timer = window.setInterval(() => setWaitingSeconds((current) => current + 1), 1000);
    return () => window.clearInterval(timer);
  }, [loading]);

  const clearConversation = () => {
    setMessages([welcomeMessage]);
    localStorage.removeItem(storageKey);
  };

  const sendMessage = async (suggested) => {
    const question = (suggested || input).trim();
    if (!question || loading) return;
    const userMessage = { role: 'user', content: question };
    const nextMessages = [...messages, userMessage].slice(-20);
    setMessages(nextMessages);
    setInput('');
    setLoading(true);
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(`${API_URL}/api/support/chat/classify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          message: question,
          normalized_message: normalizeQuestion(question),
          current_path: `${location.pathname}${location.search}`,
          condominio_id: condominioId || null,
          condominio_name: condominioName || null,
          history: nextMessages.slice(-6),
          profile,
        }),
        signal: controller.signal,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.detail || 'No fue posible consultar al capacitador');
      setMessages((current) => [...current, {
        role: 'assistant',
        content: normalizeReply(data?.reply),
        targetPath: data?.target_path || null,
        targetLabel: data?.target_label || null,
      }].slice(-20));
    } catch (error) {
      const content = error.name === 'AbortError'
        ? 'La respuesta está tardando más de lo normal. Puedes intentarlo de nuevo con una frase breve, por ejemplo: “ver incidencias pendientes”.'
        : 'No pude completar la consulta en este momento. Intenta nuevamente; si la pantalla tampoco abre, usa Soporte para reportar la falla.';
      setMessages((current) => [...current, { role: 'assistant', content }].slice(-20));
    } finally {
      window.clearTimeout(timeoutId);
      setLoading(false);
    }
  };

  const financeOffset = location.pathname.startsWith('/financiero') ? 'bottom-24 lg:bottom-28' : 'bottom-24 lg:bottom-6';

  return (
    <div className={`fixed right-3 z-[70] sm:right-6 ${financeOffset}`}>
      {open ? (
        <section className="mb-3 flex h-[min(560px,72vh)] w-[min(390px,calc(100vw-24px))] flex-col overflow-hidden rounded-3xl border border-[#d9b8be] bg-white shadow-[0_24px_70px_-18px_rgba(30,41,59,0.45)]" aria-label="Asistente AR">
          <header className="flex items-center justify-between bg-[#8A1C2C] px-4 py-3 text-white">
            <div className="flex items-center gap-3"><span className="rounded-xl bg-white/15 p-2"><Bot className="h-5 w-5" /></span><div><p className="font-semibold">Asistente AR</p><p className="text-xs text-white/75">{isCouncil ? 'Consulta y seguimiento del Consejo' : profile === 'supervisor' ? 'Seguimiento de tus condominios' : 'Operación y siguiente paso'}</p></div></div>
            <div className="flex items-center"><button onClick={clearConversation} className="rounded-lg p-2 hover:bg-white/10" title="Borrar conversación"><Trash2 className="h-4 w-4" /></button><button onClick={() => setOpen(false)} className="rounded-lg p-2 hover:bg-white/10" title="Cerrar"><X className="h-5 w-5" /></button></div>
          </header>
          <div className="border-b bg-rose-50 px-4 py-2 text-xs text-[#8A1C2C]"><Sparkles className="mr-1 inline h-3.5 w-3.5" />{condominioName || 'Selecciona un condominio'} · Nada se aplica ni se envía sin tu confirmación.</div>
          <div className="flex-1 space-y-3 overflow-y-auto p-4">
            {messages.map((message, index) => <div key={`${message.role}-${index}`} className={`max-w-[88%] whitespace-pre-line rounded-2xl px-3.5 py-2.5 text-sm leading-6 ${message.role === 'user' ? 'ml-auto bg-[#8A1C2C] text-white' : 'bg-slate-100 text-slate-800'}`}><span>{message.content}</span>{message.role === 'assistant' && message.targetPath ? <button type="button" onClick={() => { navigate(message.targetPath); setOpen(false); }} className="mt-3 flex w-full items-center justify-between rounded-xl bg-white px-3 py-2 font-semibold text-[#8A1C2C] shadow-sm ring-1 ring-slate-200 hover:bg-rose-50"><span>{message.targetLabel || 'Abrir sección'}</span><ArrowRight className="h-4 w-4" /></button> : null}</div>)}
            {loading ? <div className="rounded-2xl bg-slate-100 px-4 py-3 text-sm text-slate-600"><span className="font-medium">Consultando… {waitingSeconds}s</span><span className="mt-1 block text-xs text-slate-500">Puedes cerrar el chat y seguir trabajando; la respuesta aparecerá aquí.</span></div> : null}
            <div ref={endRef} />
          </div>
          {messages.length === 1 ? <div className="grid gap-2 px-4 pb-3 text-xs">{(isCouncil ? [
            councilCapabilities.operation ? 'Resume lo que requiere atención del Consejo esta semana' : null,
            councilCapabilities.finance ? 'Explícame la situación financiera del mes' : null,
            councilCapabilities.agreements ? '¿Qué acuerdos están vencidos o sin evidencia?' : null,
            'Quiero registrar una gestión con evidencia',
          ].filter(Boolean) : profile === 'supervisor' ? [
            'Ver incidencias que requieren seguimiento',
            'Consultar mantenimientos pendientes',
            'Revisar acuerdos vencidos o sin evidencia',
            '¿Qué puedo consultar en esta pantalla?',
          ] : [
            'Quiero registrar una incidencia',
            'Ver mantenimientos pendientes',
            'Revisar acuerdos vencidos',
            'Ayúdame a preparar un comunicado',
          ]).map((question, index) => <button key={question} onClick={() => sendMessage(question)} className={`rounded-xl border px-3 py-2 text-left hover:bg-slate-50 ${index === 2 ? 'border-violet-200 bg-violet-50 font-medium text-violet-800 hover:bg-violet-100' : ''}`}>{question}</button>)}</div> : null}
          <footer className="border-t bg-white p-3"><div className="flex gap-2"><textarea value={input} onChange={(event) => setInput(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); sendMessage(); } }} rows={2} placeholder="Escribe tu duda…" className="min-h-[46px] flex-1 resize-none rounded-xl border px-3 py-2 text-sm outline-none focus:border-violet-500" /><button onClick={() => sendMessage()} disabled={!input.trim() || loading} className="self-stretch rounded-xl bg-violet-700 px-3 text-white disabled:opacity-40"><Send className="h-4 w-4" /></button></div>{!isCouncil ? <button onClick={() => navigate('/soporte')} className="mt-2 flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-[#8A1C2C]"><LifeBuoy className="h-3.5 w-3.5" />Abrir Soporte o crear ticket</button> : null}</footer>
        </section>
      ) : null}
      <button onClick={() => setOpen((current) => !current)} className="ml-auto flex h-11 items-center gap-2 rounded-full bg-[#8A1C2C] px-4 text-sm font-semibold text-white shadow-[0_14px_38px_-10px_rgba(138,28,44,0.55)] transition hover:bg-[#681522]" aria-label="Abrir Asistente AR"><Bot className="h-5 w-5" /><span className="hidden sm:inline">Asistente AR</span></button>
    </div>
  );
}
